import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar as CalendarIcon, ExternalLink, Receipt } from "lucide-react";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/lib/auth";
import { formatMoney } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "./Drawer";
import { Tabs } from "./Tabs";
import { StatusBadge } from "./StatusBadge";
import { BookingStatusMenu } from "./BookingStatusMenu";
import { ConfirmDialog } from "./ConfirmDialog";
import { RecordPaymentDialog } from "./RecordPaymentDialog";
import { useToast } from "./Toast";
import { BookingDetailsTab } from "./booking-panel/Details";
import { BookingHistoryTab } from "./booking-panel/History";
import { BookingCommentsTab } from "./booking-panel/Comments";
import { BookingFilesTab } from "./booking-panel/Files";

type TabKey = "details" | "history" | "comments" | "files";

/**
 * Pencil component family `DSWj3` (Details), `Ul7iC` (History), `edhwT`
 * (Comments), `TUZ1P` (Files), `L7oUQ` (Verify Payment).
 *
 * Right-rail booking detail. Compose-style: header + quick stats + tabs +
 * tab body + footer. The verify-payment loop (Reject / Approve & confirm)
 * surfaces in the footer when payment.status === "verifying", otherwise
 * the footer shows Edit / Reschedule / Cancel Booking.
 */
export function BookingSidePanel({
  bookingId,
  onClose,
  onEdit,
  onReschedule,
}: {
  bookingId: string | null;
  onClose: () => void;
  onEdit?: (id: string) => void;
  onReschedule?: (id: string) => void;
}) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const qc = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState<TabKey>("details");
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["booking", bookingId],
    enabled: !!accessToken && !!bookingId,
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/bookings/{id}", {
          params: { path: { id: bookingId! } },
        }),
      ),
  });

  const verify = useMutation({
    mutationFn: async (paymentId: string) =>
      unwrap(
        await api.POST("/api/v1/payments/{id}/verify", {
          params: { path: { id: paymentId } },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["booking", bookingId] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["dashboard.overview"] });
      toast.success("Payment verified", "The booking is now confirmed.");
    },
    onError: (e) => toast.error("Verification failed", (e as Error).message),
  });

  const cancelBooking = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.PATCH("/api/v1/bookings/{id}", {
          params: { path: { id: bookingId! } },
          body: { status: "cancelled" },
        }),
      ),
    onSuccess: () => {
      setConfirmCancel(false);
      onClose();
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["dashboard.overview"] });
      toast.success("Booking cancelled", "The slot is now free.");
    },
    onError: (e) => toast.error("Couldn't cancel booking", (e as Error).message),
  });

  const isOpen = !!bookingId;
  const customerName =
    [data?.customer?.firstName, data?.customer?.lastName]
      .filter(Boolean)
      .join(" ") || "(walk-in)";
  const initials = customerName
    .split(" ")
    .map((p) => p[0]?.toUpperCase() ?? "")
    .slice(0, 2)
    .join("");
  const isVerifying = data?.payment?.status === "verifying";

  return (
    <>
      <Drawer open={isOpen} onClose={onClose} width="xl">
        {isLoading && (
          <DrawerBody className="px-5 py-6">
            <p className="text-sm text-muted-foreground">Loading…</p>
          </DrawerBody>
        )}

        {isError && (
          <DrawerBody className="px-5 py-6">
            <p className="text-sm text-destructive">{(error as Error).message}</p>
          </DrawerBody>
        )}

        {data && (
          <>
            <DrawerHeader
              title={
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground inline-flex items-center justify-center text-xs font-medium shrink-0">
                    {initials || "—"}
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-semibold tracking-tight truncate">
                      {customerName}
                    </p>
                    <p className="text-xs text-muted-foreground font-normal mt-0.5 font-mono">
                      #{data.id?.slice(0, 8)}
                      <span className="font-sans"> · {data.service?.name ?? "—"}</span>
                    </p>
                  </div>
                </div>
              }
              status={
                data.status && data.id ? (
                  <BookingStatusMenu
                    bookingId={data.id}
                    currentStatus={data.status}
                  />
                ) : null
              }
              onClose={onClose}
            />

            <div className="px-5 py-4 grid grid-cols-2 gap-4 border-b border-border">
              <Stat label="Amount" value={formatMoney(data.currency, data.price)} />
              <Stat
                label="Payment"
                value={
                  data.payment ? (
                    <StatusBadge label={data.payment.status ?? "—"} />
                  ) : (
                    "—"
                  )
                }
              />
            </div>

            <Tabs<TabKey>
              value={tab}
              onChange={setTab}
              className="mt-3 px-5"
              items={[
                { key: "details", label: "Details" },
                { key: "history", label: "History" },
                { key: "comments", label: "Comments" },
                { key: "files", label: "Files" },
              ]}
            />

            <DrawerBody className="px-5 py-5">
              {tab === "details" && (
                <BookingDetailsTab
                  // Regen widened `customFieldValues[].type` to `string`;
                  // BookingDetailsTab uses the narrower CustomFieldType
                  // union. Runtime values fit, so we cast through.
                  booking={data as Parameters<typeof BookingDetailsTab>[0]["booking"]}
                  onEdit={onEdit ? () => onEdit(data.id!) : undefined}
                />
              )}
              {tab === "history" && <BookingHistoryTab bookingId={data.id!} />}
              {tab === "comments" && <BookingCommentsTab bookingId={data.id!} />}
              {tab === "files" && <BookingFilesTab bookingId={data.id!} />}
            </DrawerBody>

            <DrawerFooter className="justify-between">
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:bg-status-bg-cancelled"
                onClick={() => setConfirmCancel(true)}
              >
                Cancel Booking
              </Button>
              <div className="flex items-center gap-2">
                {isVerifying && data.payment && (
                  <Button
                    size="sm"
                    disabled={verify.isPending}
                    onClick={() => verify.mutate(data.payment!.id!)}
                  >
                    {verify.isPending ? "Verifying…" : "✓ Approve & confirm"}
                  </Button>
                )}
                {(() => {
                  const ext = data as unknown as {
                    paymentSummary?: { remainingToConfirm?: number | string };
                  };
                  const remaining = Number(
                    ext.paymentSummary?.remainingToConfirm ?? 0,
                  );
                  // Show "Record payment" whenever the booking still owes
                  // anything toward its threshold and there's no verifying
                  // payment to approve right now.
                  return remaining > 0 && !isVerifying ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRecordOpen(true)}
                    >
                      <Receipt className="h-3.5 w-3.5 mr-1.5" /> Record Payment
                    </Button>
                  ) : null;
                })()}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!onReschedule}
                  onClick={() => onReschedule?.(data.id!)}
                >
                  <CalendarIcon className="h-3.5 w-3.5 mr-1.5" /> Reschedule
                </Button>
                <Button size="sm" disabled={!onEdit} onClick={() => onEdit?.(data.id!)}>
                  Edit
                </Button>
              </div>
            </DrawerFooter>
          </>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={() => cancelBooking.mutate()}
        title="Cancel this booking?"
        description={
          <>
            This sets the booking status to <code>cancelled</code>. The slot is
            freed and the customer is notified by email. Cancellation can be
            reversed from the History tab within 24 hours.
          </>
        }
        confirmLabel="Yes, cancel booking"
        cancelLabel="Keep booking"
        pending={cancelBooking.isPending}
      />

      {data && (() => {
        const ext = data as unknown as {
          paymentSummary?: { remainingToConfirm?: number | string };
        };
        return (
          <RecordPaymentDialog
            open={recordOpen}
            onClose={() => setRecordOpen(false)}
            bookingId={data.id!}
            bookingCurrency={data.currency ?? "PHP"}
            remainingToConfirm={Number(ext.paymentSummary?.remainingToConfirm ?? 0)}
            bookingPrice={Number(data.price ?? 0)}
          />
        );
      })()}
    </>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <div className="mt-1 flex items-center gap-1.5 text-sm font-semibold">
        {Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />}
        <span>{value}</span>
      </div>
    </div>
  );
}

/** Small "View client →" link used inside tab bodies. */
export function ViewClientLink({ id }: { id: string | undefined }) {
  if (!id) return null;
  return (
    <a
      href={`/clients?focus=${id}`}
      className="text-xs text-primary hover:underline inline-flex items-center gap-1"
    >
      View client <ExternalLink className="h-3 w-3" />
    </a>
  );
}
